import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import { Box, Button, Card, CardContent, Chip, Paper, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { localizedDate } from "../../../../utils/dayjs";
import { ListItemDeleteButton } from "../../../components/shared/ListItemDeleteButton";
import { useI18n } from "../../../hooks/useI18n";
import { useTravels } from "../../../hooks/useTravels";
import { theme } from "../../../layout/Theme";
import { TravelIframeEditor } from "./TravelIframeEditor";
import { TravelQuestionnaireAnswers } from "./TravelQuestionnaireAnswers";
import { EMPTY_TRAVEL, getTravelDestinationNames, getTravelPeriod, isTravelPast, TravelSnapshot } from "./travelTypes";

export function Travel() {
  const { locale, t } = useI18n();
  const { id } = useParams() as { id: string };
  const { travels, addTravel, updateTravel, deleteTravel } = useTravels(id);
  const [selectedTravelId, setSelectedTravelId] = useState<string>();
  const [isCreating, setIsCreating] = useState(false);
  const selectedTravel = travels.find(({ id }) => id === selectedTravelId);

  const closeEditor = useCallback(() => {
    setSelectedTravelId(undefined);
    setIsCreating(false);
  }, []);

  const saveTravel = useCallback(
    async (travel: TravelSnapshot) => {
      let saved = false;
      if (isCreating) {
        saved = await addTravel(travel);
      } else if (selectedTravelId) {
        saved = await updateTravel(selectedTravelId, travel);
      }
      if (saved) closeEditor();
    },
    [addTravel, closeEditor, isCreating, selectedTravelId, updateTravel],
  );

  const removeTravel = useCallback(
    async (travelId: string) => {
      // ListItemDeleteButton keeps the dialog open and shows an error when deleteRecord rejects
      if (!(await deleteTravel(travelId))) throw new Error("Travel deletion failed");
    },
    [deleteTravel],
  );

  if (selectedTravel || isCreating) {
    const editorTravel = selectedTravel?.travel ?? EMPTY_TRAVEL;
    const title = selectedTravel ? getTravelDestinationNames(selectedTravel.travel, t) : t("travel.new_title");
    const selectedTravelIsPast = selectedTravel ? isTravelPast(selectedTravel.travel) : false;
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={closeEditor} sx={{ pl: 0, mb: 1 }}>
          {t("travel.back_to_list")}
        </Button>
        <Stack direction="row" alignItems="center" spacing={1} useFlexGap sx={{ mb: 2 }}>
          <Typography
            variant="h5"
            component="h1"
            fontWeight={600}
            color={selectedTravelIsPast ? "text.disabled" : undefined}
          >
            {title}
          </Typography>
          {selectedTravelIsPast && <Chip label={t("travel.past")} size="small" />}
          {selectedTravel && (
            <ListItemDeleteButton
              deleteRecord={() => removeTravel(selectedTravel.id)}
              refreshCallback={closeEditor}
              title={t("travel.delete")}
              modalTitle={t("travel.delete")}
              modalContent={t("travel.delete_confirmation", { destinations: title })}
              icon={<DeleteOutlineIcon color="error" />}
              testId="deleteTravel"
              variant="outlined"
              sx={{ ml: "auto" }}
            />
          )}
        </Stack>
        <TravelIframeEditor
          key={selectedTravel?.id ?? "new-travel"}
          travel={editorTravel}
          ctaLabel={isCreating ? t("travel.create") : t("travel.update")}
          onSubmit={saveTravel}
        />
      </Box>
    );
  }

  return (
    <Paper sx={{ overflow: "hidden", backgroundColor: "inherit" }} elevation={0}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={1}
        >
          <Box>
            <Typography variant="h5" component="h1" fontWeight={600}>
              {t("travel.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("travel.count", { smart_count: travels.length })}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsCreating(true)}
          >
            {t("travel.add")}
          </Button>
        </Stack>

        <Stack spacing={0.75}>
          {travels.map((travelRecord) => {
            const destinations = getTravelDestinationNames(travelRecord.travel, t);
            const period = getTravelPeriod(travelRecord.travel);
            const stayingCondition = travelRecord.travel.journey.staying_conditions;
            const past = isTravelPast(travelRecord.travel);

            return (
              <Card
                key={travelRecord.id}
                variant="outlined"
                sx={
                  past
                    ? {
                        backgroundColor: theme.palette.neutral[200],
                        borderColor: theme.palette.neutral[400],
                        color: theme.palette.neutral[600],
                        filter: "grayscale(1)",
                        opacity: 0.68,
                        transition: "opacity 150ms ease",
                        "&:hover": { opacity: 0.82 },
                      }
                    : undefined
                }
              >
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    "&:last-child": { pb: 1.25 },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      color: past ? theme.palette.neutral[500] : theme.palette.primary[500],
                      backgroundColor: past ? theme.palette.neutral[300] : theme.palette.primary[100],
                    }}
                  >
                    <FlightTakeoffIcon fontSize="small" />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} useFlexGap flexWrap="wrap">
                      <Typography variant="body1" component="h2" fontWeight={600}>
                        {destinations}
                      </Typography>
                      {stayingCondition && (
                        <Chip
                          label={t(`travel.staying_conditions.${stayingCondition}`)}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 11, ...(past && { color: "inherit" }) }}
                        />
                      )}
                      {past && <Chip label={t("travel.past")} size="small" sx={{ height: 20, fontSize: 11 }} />}
                    </Stack>
                    {period && (
                      <Typography variant="body2" color={past ? "inherit" : "text.secondary"}>
                        {t("travel.period", {
                          startDate: localizedDate(period.startDate, locale),
                          endDate: localizedDate(period.endDate, locale),
                        })}
                      </Typography>
                    )}
                    <TravelQuestionnaireAnswers
                      answers={travelRecord.travel.conditions}
                      compact
                      muted={past}
                    />
                  </Box>

                  <Stack direction={{ xs: "column", md: "row" }} spacing={1} flexShrink={0}>
                    <Button
                      disabled={past}
                      onClick={() => setSelectedTravelId(travelRecord.id)}
                      variant="outlined"
                      size="small"
                      startIcon={<EditOutlinedIcon />}
                    >
                      {t("common.cta.edit")}
                    </Button>
                    {past ? (
                      <Button
                        disabled
                        variant="outlined"
                        size="small"
                        startIcon={<HealthAndSafetyOutlinedIcon />}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {t("travel.view_diagnostic")}
                      </Button>
                    ) : (
                      <Button
                        component={RouterLink}
                        to={`../diagnostic?travel=${encodeURIComponent(travelRecord.id)}`}
                        variant="outlined"
                        size="small"
                        startIcon={<HealthAndSafetyOutlinedIcon />}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {t("travel.view_diagnostic")}
                      </Button>
                    )}
                    <ListItemDeleteButton
                      deleteRecord={() => removeTravel(travelRecord.id)}
                      refreshCallback={closeEditor}
                      title={t("travel.delete")}
                      modalTitle={t("travel.delete")}
                      modalContent={t("travel.delete_confirmation", { destinations })}
                      icon={<DeleteOutlineIcon fontSize="small" color="error" />}
                      testId={`deleteTravel-${travelRecord.id}`}
                      type="icon"
                    />
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
}
